/**
 * +castux: session glue for the casting UX pass. game/session.ts calls into it at a few hooks
 * (key press, click, Esc, frame), so the logic lives here and the shared files only gain one-line
 * calls:
 *  - ground placement (groundPlacement.ts): enter / confirm / cancel, the circle on the frame
 *    (`RenderFrame.placement`) and the crosshair cursor (`isPlacing()`, read by the camera
 *    agent's game/cursorFeedback.ts with top priority);
 *  - error text: "Not enough <resource>" names the class resource of the pressed spell;
 *  - the GCD / cast ring at the hero's feet (`RenderFrame.castRing`) with the queue window and a
 *    "ready" pulse (setting: gameplaySettings.castRing).
 * Commands stay ordinary `cast` Commands (with `point` for ground spells).
 */
import type { CastFailReason, ContentRegistry, DeepReadonly, SimQueries, UnitId, Vec2, World } from '@keystone/sim';
import { type UiBind, abilityForBind } from '../input/bindings';
import type { RenderCastRing, RenderFrame, RenderPlacement } from '../render/types';
import { gameplaySettings } from '../hud/gameplaySettings';
import { GroundPlacement, PLACEMENT_ERRORS, checkPlacement } from './groundPlacement';
import type { CastPress, PlayerController } from './player';
import { SHOWN_ERRORS } from './player';
import { localFeedback, pressFormId, shownCast } from './prediction';
import { inQueueWindow } from './queueWindow';

/** How long the "ready" pulse lasts after a GCD / cast ends (ms, real time). */
export const READY_PULSE_MS = 260;

let placingNow = false;
/** True while a ground spell is being placed (the camera agent's cursor logic should defer to the crosshair). */
export function isPlacing(): boolean {
  return placingNow;
}

/** "Not enough Maelstrom": the first cost resource of the ability the player cannot pay, by its class name. */
export function notEnoughText(content: Pick<ContentRegistry, 'ability' | 'class'>, world: DeepReadonly<World>, actorId: UnitId, abilityId: string | null): string {
  const u = world.units[actorId];
  const ab = abilityId ? content.ability(abilityId) : undefined;
  const cls = u?.classId ? content.class(u.classId) : undefined;
  let res: string | undefined;
  for (const c of ab?.cost ?? []) {
    const have = u?.resources[c.resource]?.cur ?? 0;
    if (have < c.amount) {
      res = c.resource;
      break;
    }
  }
  res ??= ab?.cost?.[0]?.resource;
  if (!res) return SHOWN_ERRORS.no_resource!;
  const name = cls?.resources.find((r) => r.id === res)?.name ?? res.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  return `Not enough ${name}`;
}

/** Ring state from the hero's GCD / cast (pure; writes into `out`). `gcd`: the active GCD window (until <= start = none). */
export function castRingState(present: number, cast: { startedAt: number; endsAt: number; kind: 'cast' | 'channel' } | null, gcd: { start: number; until: number }, out: Pick<RenderCastRing, 'progress' | 'active' | 'queue'>): void {
  if (cast && cast.endsAt > present) {
    const total = Math.max(1, cast.endsAt - cast.startedAt);
    const done = Math.min(1, Math.max(0, (present - cast.startedAt) / total));
    out.active = true;
    out.progress = cast.kind === 'channel' ? 1 - done : done;
    // A cast queues behind its end; a channel is clipped by a new press (except its last moment).
    out.queue = inQueueWindow(cast.endsAt - present);
    return;
  }
  if (gcd.until > present && gcd.until > gcd.start) {
    out.active = true;
    out.progress = Math.min(1, Math.max(0, (present - gcd.start) / (gcd.until - gcd.start)));
    out.queue = inQueueWindow(gcd.until - present);
    return;
  }
  out.active = false;
  out.progress = 0;
  out.queue = false;
}

export interface CastUxDeps {
  content: ContentRegistry;
  queries: SimQueries;
  player: PlayerController;
  playerId: UnitId;
  world(): DeepReadonly<World>;
  onError(text: string): void;
}

export type UxPress = { handled: false } | { handled: true; press: CastPress | null };
const PASS: UxPress = { handled: false };

export class CastUx {
  readonly placement = new GroundPlacement();
  /** The bound ability of the latest key press (error text names its resource). */
  private lastAbility: string | null = null;
  private readonly circle: RenderPlacement = { x: 0, y: 0, radius: 1, valid: false };
  private readonly ring: RenderCastRing = { x: 0, y: 0, radius: 0.6, progress: 0, active: false, queue: false, pulse: 0 };
  private gcdStart = 0;
  private gcdUntil = 0;
  private wasActive = false;
  private pulseAt = -Infinity;
  private readonly gcdWin = { start: 0, until: 0 };

  constructor(private readonly d: CastUxDeps) {}

  /** A hotbar key (before the usual castBind). Handles placement; `handled: false` = cast as usual. */
  pressBind(bind: UiBind): UxPress {
    const { content, player, playerId } = this.d;
    const w = this.d.world();
    const u = w.units[playerId];
    const cls = u?.classId ? content.class(u.classId) : undefined;
    const baseId = abilityForBind(cls, bind) ?? null;
    this.lastAbility = baseId;
    if (!u || !player.acceptIntents) return PASS;
    const form = baseId ? content.ability(pressFormId(content, w, playerId, baseId)) : undefined;
    const out = this.placement.press(bind, baseId, form, gameplaySettings.get().quickCastGround);
    switch (out) {
      case 'pass':
      case 'cancel-pass':
      case 'quick':
        this.syncCursor();
        return PASS;
      case 'enter':
        this.syncCursor();
        return { handled: true, press: null };
      case 'confirm':
        return { handled: true, press: this.confirm() };
    }
  }

  /** Left click confirms, right click cancels (while placing). `handled: false` = the usual click. */
  click(button: 'left' | 'right'): UxPress {
    if (!this.placement.active) return PASS;
    if (button === 'right') {
      this.cancel();
      return { handled: true, press: null };
    }
    return { handled: true, press: this.confirm() };
  }

  /** Esc: cancels placement first (true = consumed). */
  escape(): boolean {
    return this.cancel();
  }

  cancel(): boolean {
    const had = this.placement.cancel();
    this.syncCursor();
    return had;
  }

  /** Error text for the player: "Not enough resource" names the pressed spell's resource. */
  errorText(text: string, abilityId: string | null = this.lastAbility): string {
    if (text !== SHOWN_ERRORS.no_resource) return text;
    return notEnoughText(this.d.content, this.d.world(), this.d.playerId, abilityId);
  }

  /** Error text for a sim failure reason (null = not shown). */
  errorFor(reason: CastFailReason, abilityId: string | null): string | null {
    const t = SHOWN_ERRORS[reason];
    return t ? this.errorText(t, abilityId) : null;
  }

  /**
   * Per frame, after buildFrame: the placement circle (or cancel it when it no longer applies) and
   * the cast ring. `me`: the drawn local hero (present position). `cursor`: the ground point under the mouse.
   */
  decorate(f: RenderFrame, me: { pos: Vec2; radius: number; alive: boolean } | undefined, cursor: Vec2 | null, present: number, realNow: number): void {
    const w = this.d.world();
    const u = w.units[this.d.playerId];
    f.placement = null;
    f.castRing = null;
    if (!u) return;
    const p = this.placement.active;
    if (p && (!u.alive || !this.d.player.acceptIntents)) this.cancel();
    else if (p) {
      const c = this.circle;
      const at = cursor ?? (me ? me.pos : u.pos);
      c.x = at.x;
      c.y = at.y;
      c.radius = p.radius;
      c.valid = this.check(cursor) === 'ok';
      f.placement = c;
    }
    if (!gameplaySettings.get().castRing || !u.alive || !me) {
      this.wasActive = false;
      return;
    }
    // GCD window: the sim's (start noted when gcdUntil changes) or the predicted press's.
    if (u.gcdUntil !== this.gcdUntil) {
      this.gcdUntil = u.gcdUntil;
      this.gcdStart = Math.min(w.time, u.gcdUntil);
    }
    const pred = localFeedback.prediction && localFeedback.prediction.actorId === this.d.playerId ? localFeedback.prediction : null;
    const pg = pred?.gcd && pred.gcd.until > this.gcdUntil ? pred.gcd : null;
    this.gcdWin.start = pg ? pg.start : this.gcdStart;
    this.gcdWin.until = pg ? pg.until : this.gcdUntil;
    const r = this.ring;
    castRingState(present, shownCast(w, this.d.playerId), this.gcdWin, r);
    if (this.wasActive && !r.active) this.pulseAt = realNow;
    this.wasActive = r.active;
    const since = realNow - this.pulseAt;
    r.pulse = since >= 0 && since < READY_PULSE_MS ? 1 - since / READY_PULSE_MS : 0;
    if (!r.active && r.pulse <= 0) return;
    r.x = me.pos.x;
    r.y = me.pos.y;
    r.radius = me.radius;
    f.castRing = r;
  }

  private check(point: Vec2 | null) {
    const p = this.placement.active;
    const u = this.d.world().units[this.d.playerId];
    if (!p || !u) return 'no_point' as const;
    const w = this.d.world();
    return checkPlacement(u.pos, u.combatReach, point, p.range, (q) => {
      try {
        return this.d.queries.spatial.isWalkable(w as World, q);
      } catch {
        return true;
      }
    });
  }

  /** Confirm at the cursor when valid (an ordinary cast command with the point), else say why and keep placing. */
  private confirm(): CastPress | null {
    const p = this.placement.active;
    if (!p) return null;
    const point = this.d.player.cursorPoint;
    const ok = this.check(point);
    if (ok !== 'ok') {
      this.d.onError(PLACEMENT_ERRORS[ok]);
      return null;
    }
    this.placement.take();
    this.syncCursor();
    this.lastAbility = p.abilityId;
    return this.d.player.castAbility(this.d.world(), p.abilityId, { x: point!.x, y: point!.y });
  }

  private syncCursor(): void {
    placingNow = !!this.placement.active;
  }

  dispose(): void {
    this.placement.cancel();
    this.syncCursor();
  }
}
